/**
 * ZIP Format Processor
 *
 * Handles ZIP file creation and extraction for complete trip exports/imports
 * Includes media file handling and structured data organization
 */

import JSZip from 'jszip';

class ZipFormatProcessor {
  constructor() {
    this.maxFileSize = 100 * 1024 * 1024; // 100MB
    this.supportedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    this.supportedDocTypes = ['application/pdf', 'text/plain', 'application/msword'];
  }

  /**
   * Create ZIP export from trip data
   *
   * @param {Object} tripData - Complete trip data
   * @param {Object} options - Export options
   * @returns {Promise<Blob>} ZIP file as blob
   */
  async createTripExport(tripData, options = {}) {
    const zip = new JSZip();

    // Create folder structure
    const tripDataFolder = zip.folder('trip-data');
    const exportsFolder = zip.folder('exports');
    const documentsFolder = zip.folder('documents');
    const mediaFolder = zip.folder('media');

    // Add trip data as JSON files
    await this.addTripDataFiles(tripDataFolder, tripData);

    // Add CSV exports
    await this.addCsvExports(exportsFolder, tripData);

    // Add documents and media (if available)
    if (options.includeDocuments && tripData.documents) {
      await this.addDocumentFiles(documentsFolder, mediaFolder, tripData.documents);
    }

    // Add README and manifest
    this.addReadmeFile(zip, tripData);
    this.addManifestFile(zip, tripData, options);

    // Generate ZIP blob
    return await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
  }

  /**
   * Extract and parse ZIP import file
   *
   * @param {File} zipFile - ZIP file to extract
   * @returns {Promise<Object>} Parsed import data
   */
  async extractTripImport(zipFile) {
    const zip = await JSZip.loadAsync(zipFile);

    // Parse manifest first to understand structure
    const manifest = await this.parseManifest(zip);

    // Extract trip data
    const tripData = await this.extractTripData(zip, manifest);

    // Extract document information (files themselves handled separately)
    const documentInfo = await this.extractDocumentInfo(zip);

    return {
      manifest,
      tripData,
      documentInfo,
      zipInstance: zip // Keep reference for document extraction
    };
  }

  /**
   * Add trip data as JSON files to ZIP
   */
  async addTripDataFiles(folder, tripData) {
    const dataFiles = {
      'trip-info.json': tripData.trip,
      'days.json': tripData.days || [],
      'places.json': tripData.places || [],
      'accommodation.json': tripData.accommodation || [],
      'travel.json': tripData.travel || [],
      'documents.json': tripData.documents || [],
      'links.json': tripData.links || []
    };

    Object.entries(dataFiles).forEach(([filename, data]) => {
      folder.file(filename, JSON.stringify(data, null, 2));
    });

    if (tripData._meta?.linkRegistry) {
      folder.file('link-registry-config.json', JSON.stringify(tripData._meta.linkRegistry, null, 2));
    }
  }

  /**
   * Add CSV exports to ZIP
   */
  async addCsvExports(folder, tripData) {
    // Itinerary CSV
    if (tripData.days && tripData.days.length > 0) {
      const itineraryCsv = this.generateItineraryCsv(tripData.days);
      folder.file('itinerary.csv', itineraryCsv);
    }

    // Places CSV
    if (tripData.places && tripData.places.length > 0) {
      const placesCsv = this.generatePlacesCsv(tripData.places);
      folder.file('places.csv', placesCsv);
    }

    // Accommodation CSV
    if (tripData.accommodation && tripData.accommodation.length > 0) {
      const accommodationCsv = this.generateAccommodationCsv(tripData.accommodation);
      folder.file('accommodation.csv', accommodationCsv);
    }

    // Travel CSV
    if (tripData.travel && tripData.travel.length > 0) {
      const travelCsv = this.generateTravelCsv(tripData.travel);
      folder.file('travel.csv', travelCsv);
    }
  }

  /**
   * Add document and media files to ZIP
   * Fetches actual binary files from Firebase Storage and routes them to appropriate folders
   */
  async addDocumentFiles(documentsFolder, mediaFolder, documents, storageFiles = []) {
    console.log(`[ZIP] Adding ${documents.length} documents and ${storageFiles.length} storage files to ZIP`);

    let documentsAdded = 0;
    let mediaAdded = 0;
    let errors = 0;

    // Process storage files fetched from Firebase Storage
    if (storageFiles && storageFiles.length > 0) {
      for (const fileData of storageFiles) {
        try {
          const isImage = this.isImageFile(fileData);
          const folder = isImage ? mediaFolder : documentsFolder;
          const fileName = fileData.originalName || fileData.name;

          console.log(`[ZIP] Adding ${isImage ? 'image' : 'document'}: ${fileName} (${fileData.blob.size} bytes)`);

          // Add blob to appropriate folder
          folder.file(fileName, fileData.blob);

          if (isImage) {
            mediaAdded++;
          } else {
            documentsAdded++;
          }
        } catch (error) {
          console.error(`[ZIP] Error adding file ${fileData.originalName}:`, error);
          errors++;
        }
      }
    }

    // Create README files in each folder
    if (documentsAdded > 0) {
      documentsFolder.file('README.txt',
        'Document Files\n\n' +
        'This folder contains your trip-related document files (PDFs, text files, etc.).\n\n' +
        `Total documents included: ${documentsAdded}\n\n` +
        'Files:\n' +
        documents
          .filter(doc => !this.isImageFileByMimeType(doc.mimeType))
          .map(doc => `- ${doc.name}`)
          .join('\n')
      );
    } else {
      documentsFolder.file('README.txt',
        'Document Files\n\n' +
        'No document files were found for this trip.\n'
      );
    }

    if (mediaAdded > 0) {
      mediaFolder.file('README.txt',
        'Media Files\n\n' +
        'This folder contains images and media files associated with your trip.\n\n' +
        `Total media files included: ${mediaAdded}\n\n` +
        'Files:\n' +
        documents
          .filter(doc => this.isImageFileByMimeType(doc.mimeType))
          .map(doc => `- ${doc.name}`)
          .join('\n')
      );
    } else {
      mediaFolder.file('README.txt',
        'Media Files\n\n' +
        'No media files were found for this trip.\n'
      );
    }

    console.log(`[ZIP] File addition complete: ${documentsAdded} documents, ${mediaAdded} media, ${errors} errors`);
  }

  /**
   * Check if file data is an image based on blob or metadata
   */
  isImageFile(fileData) {
    // Check blob type first
    if (fileData.blob && fileData.blob.type) {
      return fileData.blob.type.startsWith('image/');
    }

    // Fallback to file name extension
    const name = fileData.originalName || fileData.name || '';
    const ext = name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  }

  /**
   * Check if MIME type is an image
   */
  isImageFileByMimeType(mimeType) {
    return mimeType && mimeType.startsWith('image/');
  }

  /**
   * Add README file
   */
  addReadmeFile(zip, tripData) {
    const trip = tripData.trip || {};
    const readme = `# Trip Export: ${trip.name || 'My Trip'}

## Export Information
- Export Date: ${new Date().toISOString()}
- Export Version: 1.0.0
- Trip ID: ${trip.id}

## Folder Structure
- \`trip-data/\` - Complete trip data in JSON format
  - trip-info.json - Basic trip information
  - days.json - Day-by-day itinerary
  - places.json - Places and locations
  - accommodation.json - Hotel and accommodation details
  - travel.json - Flight, train, and transportation bookings
  - documents.json - Document metadata and associations

- \`exports/\` - CSV exports for spreadsheet applications
  - itinerary.csv - Day-by-day schedule
  - places.csv - Location information
  - accommodation.csv - Accommodation bookings
  - travel.csv - Transportation details

- \`documents/\` - Trip-related document files (PDFs, confirmations, etc.)
- \`media/\` - Images and media files associated with the trip

## Trip Overview
- **Destination**: ${trip.destination || 'N/A'}
- **Dates**: ${trip.startDate || 'N/A'} to ${trip.endDate || 'N/A'}
- **Days**: ${tripData.days?.length || 0}
- **Places**: ${tripData.places?.length || 0}
- **Accommodations**: ${tripData.accommodation?.length || 0}
- **Travel Arrangements**: ${tripData.travel?.length || 0}
- **Documents**: ${tripData.documents?.length || 0}

## How to Use This Export

### For Backup and Restore
1. Keep this ZIP file as a complete backup of your trip
2. To restore, use the Trip Import feature in Trip Planner
3. The JSON files contain all data needed for complete restoration

### For External Use
1. Open CSV files in Excel, Google Sheets, or other spreadsheet applications
2. Document and media files can be accessed directly from their folders
3. The JSON files can be used by other applications or for data analysis

### Import Back to Trip Planner
1. Use File > Import Trip in the Trip Planner application
2. Select this ZIP file
3. Choose import options (New Trip, Merge, or Replace)
4. All data relationships and associations will be preserved

## Technical Information
- Export Format Version: 1.0.0
- Compatible with Trip Planner v2025.1+
- All data exported in UTF-8 encoding
- ZIP compression level: 6 (balanced size/speed)

Generated by Trip Planner - https://tripplanner.example.com
Export completed: ${new Date().toLocaleString()}`;

    zip.file('README.txt', readme);
  }

  /**
   * Add manifest file with export metadata
   */
  addManifestFile(zip, tripData, options) {
    const manifest = {
      exportMetadata: {
        formatVersion: '1.0.0',
        exportDate: new Date().toISOString(),
        exportTool: 'Trip Planner',
        exportToolVersion: '2025.1',
        tripId: tripData.trip?.id,
        exportType: 'complete_trip'
      },
      tripSummary: {
        name: tripData.trip?.name,
        destination: tripData.trip?.destination,
        startDate: tripData.trip?.startDate,
        endDate: tripData.trip?.endDate,
        travelers: tripData.trip?.travelers
      },
      contentSummary: {
        days: tripData.days?.length || 0,
        places: tripData.places?.length || 0,
        accommodation: tripData.accommodation?.length || 0,
        travel: tripData.travel?.length || 0,
        documents: tripData.documents?.length || 0
      },
      exportOptions: {
        includeDocuments: options.includeDocuments !== false,
        includeImages: options.includeImages !== false,
        compressionLevel: 6
      },
      fileStructure: {
        'trip-data/': 'JSON data files',
        'exports/': 'CSV exports for spreadsheet applications',
        'documents/': 'Trip-related document files',
        'media/': 'Images and media files',
        'README.txt': 'Human-readable export information',
        'export-manifest.json': 'Machine-readable export metadata'
      }
    };

    zip.file('export-manifest.json', JSON.stringify(manifest, null, 2));
  }

  /**
   * Parse manifest from ZIP
   */
  async parseManifest(zip) {
    try {
      const manifestFile = zip.file('export-manifest.json');
      if (manifestFile) {
        const manifestContent = await manifestFile.async('string');
        return JSON.parse(manifestContent);
      }
    } catch (error) {
      console.warn('Could not parse manifest, using defaults', error);
    }

    return {
      exportMetadata: { formatVersion: '1.0.0', exportType: 'unknown' },
      contentSummary: {}
    };
  }

  /**
   * Extract trip data from ZIP
   */
  async extractTripData(zip, manifest) {
    const tripData = {};

    const dataFiles = [
      'trip-info.json',
      'days.json',
      'places.json',
      'accommodation.json',
      'travel.json',
      'documents.json',
      'links.json',
      'link-registry-config.json'
    ];

    for (const filename of dataFiles) {
      try {
        const file = zip.file(`trip-data/${filename}`);
        if (!file) {
          continue;
        }
        const content = await file.async('string');
        if (filename === 'trip-info.json') {
          tripData.trip = JSON.parse(content);
        } else if (filename === 'link-registry-config.json') {
          tripData._meta = { linkRegistry: JSON.parse(content) };
        } else if (filename === 'links.json') {
          tripData.links = JSON.parse(content);
        } else {
          const key = filename.replace('.json', '');
          tripData[key] = JSON.parse(content);
        }
      } catch (error) {
        console.warn(`Could not extract ${filename}:`, error);
      }
    }

    return tripData;
  }

  /**
   * Extract document information
   */
  async extractDocumentInfo(zip) {
    const documentFiles = [];
    const mediaFiles = [];

    // List files in documents folder
    zip.folder('documents').forEach((relativePath, file) => {
      if (!file.dir && relativePath !== 'README.txt') {
        documentFiles.push({
          name: relativePath,
          size: file._data?.uncompressedSize || 0,
          type: this.getFileType(relativePath)
        });
      }
    });

    // List files in media folder
    zip.folder('media').forEach((relativePath, file) => {
      if (!file.dir && relativePath !== 'README.txt') {
        mediaFiles.push({
          name: relativePath,
          size: file._data?.uncompressedSize || 0,
          type: this.getFileType(relativePath)
        });
      }
    });

    return {
      documents: documentFiles,
      media: mediaFiles
    };
  }

  /**
   * Generate CSV content helpers
   */
  generateItineraryCsv(days) {
    const headers = ['Day', 'Date', 'Title', 'Activities', 'Notes', 'Budget'];
    let csv = headers.join(',') + '\n';

    days.forEach(day => {
      const row = [
        day.dayNumber || '',
        day.date || '',
        `"${(day.title || '').replace(/"/g, '""')}"`,
        `"${(Array.isArray(day.activities) ? day.activities.join('; ') : day.activities || '').replace(/"/g, '""')}"`,
        `"${(day.notes || '').replace(/"/g, '""')}"`,
        day.budget || ''
      ];
      csv += row.join(',') + '\n';
    });

    return csv;
  }

  generatePlacesCsv(places) {
    const headers = ['Name', 'Type', 'Address', 'Visit Date', 'Duration', 'Notes'];
    let csv = headers.join(',') + '\n';

    places.forEach(place => {
      const row = [
        `"${(place.name || '').replace(/"/g, '""')}"`,
        place.type || '',
        `"${(place.address || '').replace(/"/g, '""')}"`,
        place.visitDate || '',
        place.duration || '',
        `"${(place.notes || '').replace(/"/g, '""')}"`
      ];
      csv += row.join(',') + '\n';
    });

    return csv;
  }

  generateAccommodationCsv(accommodation) {
    const headers = ['Name', 'Type', 'Address', 'Check In', 'Check Out', 'Nights', 'Guests', 'Room Type', 'Total Cost', 'Confirmation'];
    let csv = headers.join(',') + '\n';

    accommodation.forEach(accom => {
      const row = [
        `"${(accom.name || '').replace(/"/g, '""')}"`,
        accom.type || '',
        `"${(accom.address || '').replace(/"/g, '""')}"`,
        accom.checkIn || '',
        accom.checkOut || '',
        accom.nights || '',
        accom.guests || '',
        `"${(accom.roomType || '').replace(/"/g, '""')}"`,
        accom.totalCost ? `${accom.totalCost} ${accom.currency}` : '',
        accom.confirmationNumber || ''
      ];
      csv += row.join(',') + '\n';
    });

    return csv;
  }

  generateTravelCsv(travel) {
    const headers = ['Type', 'Details', 'Departure', 'Arrival', 'Duration', 'Cost', 'Booking Reference'];
    let csv = headers.join(',') + '\n';

    travel.forEach(trip => {
      const details = trip.airline ? `${trip.airline} ${trip.flightNumber}` : trip.operator || '';
      const departure = trip.departure ? `${trip.departure.city} ${trip.departure.date} ${trip.departure.time}` : '';
      const arrival = trip.arrival ? `${trip.arrival.city} ${trip.arrival.date} ${trip.arrival.time}` : '';
      const cost = trip.cost ? `${trip.cost} ${trip.currency}` : '';

      const row = [
        trip.type || '',
        `"${details.replace(/"/g, '""')}"`,
        `"${departure.replace(/"/g, '""')}"`,
        `"${arrival.replace(/"/g, '""')}"`,
        trip.duration || '',
        cost,
        trip.bookingReference || trip.confirmationNumber || ''
      ];
      csv += row.join(',') + '\n';
    });

    return csv;
  }

  /**
   * Utility methods
   */
  getFileType(filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    const typeMap = {
      pdf: 'application/pdf',
      txt: 'text/plain',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp'
    };
    return typeMap[ext] || 'application/octet-stream';
  }

  isImageFile(doc) {
    return this.supportedImageTypes.includes(doc.type || '');
  }

  /**
   * Download ZIP file
   */
  downloadZipFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}

export default new ZipFormatProcessor();